import {
  gql,
  useMutation as useDeprecatedMutation,
  useQuery,
} from "@apollo/client";
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Grid,
  makeStyles,
  StyleRules,
  TextField,
  Theme,
} from "@material-ui/core";
import { EMAIL_PLACEHOLDERS } from "@resource/common";
import * as Sentry from "@sentry/nextjs";
import DialogTitle from "components/legacy/Dialog/DialogTitle/DialogTitle";
import { useFlashMessage } from "components/legacy/FlashMessage/FlashMessage";
import Loading from "components/legacy/Loading/Loading";
import {
  CreateEmailTemplate,
  CreateEmailTemplateVariables,
  CurrentUserForEmailTemplate,
  GetEmailTemplateForEmailTemplateModal,
  GetEmailTemplateForEmailTemplateModalVariables,
  UpdateEmailTemplate,
  UpdateEmailTemplateVariables,
} from "generated/schemaTypes";
import _ from "lodash";
import dynamic from "next/dynamic";
import { Suspense, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

const CURRENT_USER_PRISMA_FOR_EMAIL_TEMPLATE = gql`
  query CurrentUserForEmailTemplate {
    currentUserPrisma {
      id
      name: fullName
      streamToken
      photo: imageUrl
      currentOrganization {
        id
        customVariableOptions
      }
    }
  }
`;

const GET_EMAIL_TEMPLATE_FOR_EMAIL_TEMPLATE_MODAL = gql`
  query GetEmailTemplateForEmailTemplateModal($id: ID!) {
    currentOrganization {
      id
      emailTemplateById(id: $id) {
        id
        name
        subject
        body
      }
    }
  }
`;

const UPDATE_EMAIL_TEMPLATE = gql`
  mutation UpdateEmailTemplate($input: UpdateEmailTemplateInput!) {
    updateEmailTemplate(input: $input) {
      emailTemplate {
        id
        name
        subject
        body
        updatedAt
        creator {
          id
        }
      }
    }
  }
`;

const CREATE_EMAIL_TEMPLATE = gql`
  mutation CreateEmailTemplate($input: CreateEmailTemplateInput!) {
    createEmailTemplate(input: $input) {
      emailTemplate {
        id
        name
        subject
        updatedAt
        creator {
          id
        }
      }
    }
  }
`;

export type EmailTemplateToDuplicate = {
  id: string;
  name: string;
  subject: string;
  body: string;
};

const RichTextEditor = dynamic(
  () => import("components/legacy/RichTextEditor/RichTextEditor"),
  { ssr: false }
);

const useStyles = makeStyles(
  (theme: Theme): StyleRules =>
    createStyles({
      actions: {
        marginTop: "5px",
        marginLeft: "375px",
        marginBottom: "15px",
        marginRight: theme.spacing(3),
        padding: 0,
      },
      dialogContent: {
        overflowY: "visible",
      },
    })
);

type EmailTemplateModalProps = {
  emailTemplateId: string | undefined;
  emailTemplateToDuplicate?: EmailTemplateToDuplicate;
  onClose: () => void;
  open: boolean;
  afterSubmit?(): void;
};

function EmailTemplateModal({
  open,
  onClose,
  emailTemplateId,
  emailTemplateToDuplicate,
  afterSubmit,
}: EmailTemplateModalProps) {
  const {
    register,
    control,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm();
  const { setContent } = useFlashMessage();
  const { data } = useQuery<CurrentUserForEmailTemplate>(
    CURRENT_USER_PRISMA_FOR_EMAIL_TEMPLATE
  );
  const user = data?.currentUserPrisma;
  const classes = useStyles();

  const { data: emailTemplateData, loading } = useQuery<
    GetEmailTemplateForEmailTemplateModal,
    GetEmailTemplateForEmailTemplateModalVariables
  >(GET_EMAIL_TEMPLATE_FOR_EMAIL_TEMPLATE_MODAL, {
    skip: !emailTemplateId,
    variables: {
      id: emailTemplateId || "",
    },
  });

  const [createEmailTemplate, { loading: creatingEmailTemplate }] =
    useDeprecatedMutation<CreateEmailTemplate, CreateEmailTemplateVariables>(
      CREATE_EMAIL_TEMPLATE
    );

  const [updateEmailTemplate, { loading: updatingEmailTemplate }] =
    useDeprecatedMutation<UpdateEmailTemplate, UpdateEmailTemplateVariables>(
      UPDATE_EMAIL_TEMPLATE
    );

  useEffect(() => {
    const template = emailTemplateData?.currentOrganization?.emailTemplateById;
    if (template) {
      const { name, subject, body } = template;
      reset({
        name,
        subject,
        body,
      });
    } else if (emailTemplateToDuplicate) {
      const { name, subject, body } = emailTemplateToDuplicate;
      reset({
        name: `Copy of ${name}`,
        subject,
        body,
      });
    }
  }, [emailTemplateData, emailTemplateToDuplicate, reset]);

  const handleClose = () => {
    reset({
      name: "",
      subject: "",
      body: "",
    });
    onClose();
  };

  const onSubmit = async ({ body, subject, name }: Record<string, string>) => {
    if (!user) {
      setContent({
        content: "Something went wrong creating this Email Template",
        severity: "error",
      });
      Sentry.captureMessage("User was not found when creating Email Template");
      return;
    }

    const refetchQueries = ["CurrentOrganizationEmailTemplates"];
    if (emailTemplateId) {
      await updateEmailTemplate({
        variables: {
          input: {
            id: emailTemplateId,
            body,
            name,
            subject,
          },
        },
        refetchQueries,
      });
    } else {
      await createEmailTemplate({
        variables: {
          input: {
            body,
            name,
            subject,
          },
        },
        refetchQueries,
      });
    }
    afterSubmit?.();
    handleClose();
  };

  const { ref: nameFieldRef, ...nameFieldProps } = register("name", {
    required: true,
  });

  const placeholders = [
    ...EMAIL_PLACEHOLDERS,
    ..._.map(
      data?.currentUserPrisma?.currentOrganization?.customVariableOptions,
      (option) => ({
        id: option,
        label: option,
      })
    ),
  ];
  let title;
  if (emailTemplateId) {
    title = "Edit email template";
  } else {
    title = `${
      emailTemplateToDuplicate ? "Duplicate" : "Create"
    } email template`;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth scroll="body">
      <DialogTitle onClose={handleClose}>{title}</DialogTitle>
      <Divider />
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent className={classes.dialogContent}>
          {loading ? (
            <Loading />
          ) : (
            <Grid container direction="column" spacing={2}>
              <Grid item>
                <TextField
                  {...nameFieldProps}
                  inputRef={nameFieldRef}
                  variant="outlined"
                  margin="dense"
                  label="Template Name"
                  defaultValue=""
                  error={!!errors.name}
                  fullWidth
                />
              </Grid>
              <Suspense fallback={<Loading />}>
                <Grid item>
                  <Controller
                    name="subject"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <RichTextEditor
                        label="Subject"
                        toolbar="top"
                        formats={["placeholder"]}
                        value={field.value}
                        error={!!errors.subject}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholders={placeholders}
                        trimEmpty
                        toolbarFormats={{
                          placeholder: true,
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item>
                  <Controller
                    name="body"
                    control={control}
                    defaultValue=""
                    rules={{ required: true }}
                    render={({ field }) => (
                      <RichTextEditor
                        label="Body"
                        toolbar="top"
                        error={!!errors.body}
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholders={placeholders}
                        placeholder="Hello 👋"
                        trimEmpty
                        height={240}
                      />
                    )}
                  />
                </Grid>
              </Suspense>
            </Grid>
          )}
        </DialogContent>
        <DialogActions className={classes.actions}>
          <Button
            color="primary"
            variant="contained"
            size="large"
            type="submit"
            fullWidth
            disabled={creatingEmailTemplate || updatingEmailTemplate}
          >
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
export default EmailTemplateModal;
